import React, { useState, useEffect } from 'react';
import {
  TextField,
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Button,
  Autocomplete,
} from '@mui/material';
import { ClearAll } from '@mui/icons-material';
import { loadEmployees, Employee, getEmployeeDisplayLabel, isManagerInHierarchy } from '../../utils/employeeData';

interface BasicDetailsData {
  estimatedContractAmount: string;
  requestDate: string;
  requestorName: string;
  requestorCode?: string;
  requestorEmail?: string;
  requestorManager: string;
  requestorManagerCode?: string;
  requestorManagerEmail?: string;
  companyName: string;
  streetAddress: string;
  addressLine2: string;
  city: string;
  zipCode: string;
  state: string;
  country: string;
  phone: string;
  contactPerson: string;
  contactEmail: string;
  vendorWebsite: string;
  howLearnedAbout: string;
  goodsServicesDescription: string;
  competitiveBidsObtained: string;
}

interface BasicDetailsFormProps {
  data: BasicDetailsData;
  onChange: (data: BasicDetailsData) => void;
  disabled?: boolean;
  errors?: { field: string; message: string }[];
}

const BasicDetailsForm: React.FC<BasicDetailsFormProps> = ({ data, onChange, disabled = false, errors = [] }) => {
  const getFieldError = (fieldName: string) => {
    return errors.find(err => err.field === fieldName)?.message;
  };
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [managerError, setManagerError] = useState<string>('');

  useEffect(() => {
    const loadData = async () => {
      const employeeData = await loadEmployees();
      setEmployees(employeeData);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    // Validate manager hierarchy whenever requestor or manager changes
    if (data.requestorCode && data.requestorManagerCode && employees.length > 0) {
      const isValid = isManagerInHierarchy(employees, data.requestorCode, data.requestorManagerCode);
      if (!isValid) {
        setManagerError('Requestor\'s Manager doesn\'t follow hierarchical structure. Please verify.');
      } else {
        setManagerError('');
      }
    } else {
      setManagerError('');
    }
  }, [data.requestorCode, data.requestorManagerCode, employees]);

  const handleChange = (field: keyof BasicDetailsData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({
      ...data,
      [field]: event.target.value,
    });
  };

  const handleEmployeeChange = (field: 'requestorName' | 'requestorManager', employee: Employee | null) => {
    if (field === 'requestorName') {
      onChange({
        ...data,
        requestorName: employee ? employee.employeeName : '',
        requestorCode: employee ? employee.employeeCode : '',
        requestorEmail: employee ? employee.workEmail : '',
      });
    } else {
      onChange({
        ...data,
        requestorManager: employee ? employee.employeeName : '',
        requestorManagerCode: employee ? employee.employeeCode : '',
        requestorManagerEmail: employee ? employee.workEmail : '',
      });
    }
  };

  const handleClear = () => {
    onChange({
      estimatedContractAmount: '',
      requestDate: '',
      requestorName: '',
      requestorCode: '',
      requestorEmail: '',
      requestorManager: '',
      requestorManagerCode: '',
      requestorManagerEmail: '',
      companyName: '',
      streetAddress: '',
      addressLine2: '',
      city: '',
      zipCode: '',
      state: '',
      country: '',
      phone: '',
      contactPerson: '',
      contactEmail: '',
      vendorWebsite: '',
      howLearnedAbout: '',
      goodsServicesDescription: '',
      competitiveBidsObtained: '',
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">A. Basic Details</Typography>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ClearAll />}
          onClick={handleClear}
          size="small"
        >
          Clear Form
        </Button>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            required
            fullWidth
            label="Estimated Total Contract Value (USD)"
            value={data.estimatedContractAmount}
            onChange={handleChange('estimatedContractAmount')}
            variant="outlined"
            disabled={disabled}
            error={!!getFieldError('estimatedContractAmount')}
            helperText={getFieldError('estimatedContractAmount')}
          />
          <TextField
            required
            fullWidth
            type="date"
            label="Request Date"
            value={data.requestDate}
            onChange={handleChange('requestDate')}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            disabled={disabled}
            error={!!getFieldError('requestDate')}
            helperText={getFieldError('requestDate')}
          />
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>
          1. Internal Request Information
        </Typography>

        <Autocomplete
          fullWidth
          options={employees}
          getOptionLabel={(employee) => getEmployeeDisplayLabel(employee)}
          value={employees.find(emp => emp.employeeCode === data.requestorCode) || null}
          onChange={(_, newValue) => handleEmployeeChange('requestorName', newValue)}
          loading={loading}
          disabled={true}
          readOnly={true}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Requestor Name (Auto-filled)"
              placeholder="Auto-filled with your name"
              InputProps={{
                ...params.InputProps,
                readOnly: true,
              }}
              helperText={getFieldError('requestorName') || "This field is locked to the logged-in user"}
              error={!!getFieldError('requestorName')}
            />
          )}
          isOptionEqualToValue={(option, value) => option.employeeCode === value.employeeCode}
          componentsProps={{
            popper: {
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    altAxis: true,
                    rootBoundary: 'document',
                  },
                },
              ],
            },
          }}
        />

        <Autocomplete
          fullWidth
          disablePortal
          options={employees}
          getOptionLabel={(employee) => getEmployeeDisplayLabel(employee)}
          value={employees.find(emp => emp.employeeCode === data.requestorManagerCode) || null}
          onChange={(_, newValue) => handleEmployeeChange('requestorManager', newValue)}
          loading={loading}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="1.2 Approving Manager"
              variant="outlined"
              helperText={getFieldError('requestorManager') || managerError || 'Select from employee list'}
              error={!!getFieldError('requestorManager') || !!managerError}
            />
          )}
          isOptionEqualToValue={(option, value) => option.employeeCode === value.employeeCode}
          componentsProps={{
            popper: {
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    altAxis: true,
                    rootBoundary: 'document',
                  },
                },
              ],
            },
          }}
        />

        {managerError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {managerError}
          </Alert>
        )}

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>
          2. New Vendor, Supplier, or Service Provider Information
        </Typography>

        <TextField
          required
          fullWidth
          label="2.1 Company Name"
          value={data.companyName}
          onChange={handleChange('companyName')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('companyName')}
          helperText={getFieldError('companyName')}
        />

        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          2.2 Address Information
        </Typography>

        <TextField
          required
          fullWidth
          label="Street Name and Number"
          value={data.streetAddress}
          onChange={handleChange('streetAddress')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('streetAddress')}
          helperText={getFieldError('streetAddress')}
        />

        <TextField
          fullWidth
          label="Address Line 2 (if needed)"
          value={data.addressLine2}
          onChange={handleChange('addressLine2')}
          variant="outlined"
          disabled={disabled}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            required
            fullWidth
            label="City"
            value={data.city}
            onChange={handleChange('city')}
            variant="outlined"
            disabled={disabled}
            error={!!getFieldError('city')}
            helperText={getFieldError('city')}
          />
          <TextField
            required
            fullWidth
            label="State/Province"
            value={data.state}
            onChange={handleChange('state')}
            variant="outlined"
            disabled={disabled}
            error={!!getFieldError('state')}
            helperText={getFieldError('state')}
          />
          <TextField
            required
            fullWidth
            label="ZIP (Postal) Code"
            value={data.zipCode}
            onChange={handleChange('zipCode')}
            variant="outlined"
            disabled={disabled}
            error={!!getFieldError('zipCode')}
            helperText={getFieldError('zipCode')}
          />
        </Box>

        <TextField
          required
          fullWidth
          label="Country"
          value={data.country}
          onChange={handleChange('country')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('country')}
          helperText={getFieldError('country')}
        />

        <TextField
          required
          fullWidth
          label="2.3 Phone"
          value={data.phone}
          onChange={handleChange('phone')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('phone')}
          helperText={getFieldError('phone')}
        />

        <TextField
          required
          fullWidth
          label="2.4 Contact Person"
          value={data.contactPerson}
          onChange={handleChange('contactPerson')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('contactPerson')}
          helperText={getFieldError('contactPerson')}
        />

        <TextField
          required
          fullWidth
          type="email"
          label="2.5 Contact Email Address"
          value={data.contactEmail}
          onChange={handleChange('contactEmail')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('contactEmail')}
          helperText={getFieldError('contactEmail')}
        />

        <TextField
          required
          fullWidth
          label="2.6 Vendor Website"
          value={data.vendorWebsite}
          onChange={handleChange('vendorWebsite')}
          variant="outlined"
          disabled={disabled}
          placeholder="https://example.com"
          error={!!getFieldError('vendorWebsite')}
          helperText={getFieldError('vendorWebsite')}
        />

        <TextField
          required
          fullWidth
          multiline
          rows={3}
          label="3. How did you learn about this third party?"
          value={data.howLearnedAbout}
          onChange={handleChange('howLearnedAbout')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('howLearnedAbout')}
          helperText={getFieldError('howLearnedAbout')}
        />

        <TextField
          required
          fullWidth
          multiline
          rows={3}
          label="4. What goods or services will this third party provide?"
          value={data.goodsServicesDescription}
          onChange={handleChange('goodsServicesDescription')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('goodsServicesDescription')}
          helperText={getFieldError('goodsServicesDescription')}
        />

        <FormControl component="fieldset" disabled={disabled} error={!!getFieldError('competitiveBidsObtained')}>
          <FormLabel component="legend">
            5. Were competitive bids or other pricing comparisons obtained?
          </FormLabel>
          <RadioGroup
            value={data.competitiveBidsObtained}
            onChange={handleChange('competitiveBidsObtained')}
          >
            <FormControlLabel value="YES" control={<Radio />} label="YES" />
            <FormControlLabel value="NO" control={<Radio />} label="NO" />
          </RadioGroup>
          {getFieldError('competitiveBidsObtained') && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {getFieldError('competitiveBidsObtained')}
            </Typography>
          )}
          <Typography variant="caption" sx={{ mt: 1 }}>
            If YES, then please provide the information in the "Bids" Tab.<br />
            If NO, then please explain why not in Box 4 in the "Bids" Tab.
          </Typography>
        </FormControl>
      </Box>
    </Box>
  );
};

export default BasicDetailsForm;
